import { FC, useEffect, useState } from "react";
import { Box, CircularProgress, Grid, Stack, Typography, Skeleton, Divider, Paper, Drawer, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { Utils } from "../../utils/common-utils";
import moment from 'moment';
import { toast } from 'react-toastify';
import { SalaryNavigationBar } from '../../components/Salary/SalaryNavigationBar';
import { fetchSalaries, paySalary, payDailyWage, deleteSalary, fetchSalaryById, fetchSalarySlipUrl, downloadSalarySlip } from '../../store/salaryStore/salaryThunk';
import DialogShareSalarySlip from '../../components/DialogShareSalarySlip/DialogShareSalarySlip';

// Enhanced Salary View page with navigation and filters
const SalaryView: FC = () => {
  const { salaryId } = useParams();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { isLoggedIn, selectedAccount } = useAppSelector((state) => state.auth);
  const { salaries } = useAppSelector(state => state.salaries);

  const [pdfUrl, setPdfUrl] = useState<string>("");
  const [salaryDetails, setSalaryDetails] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [currentSalaryId, setCurrentSalaryId] = useState(salaryId);
  const [shareDrawerOpen, setShareDrawerOpen] = useState(false);
  const [selectedSalaryForShare, setSelectedSalaryForShare] = useState<any>(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreData, setHasMoreData] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const pageSize = 20;

  // Update currentSalaryId when URL params change
  useEffect(() => {
    if (salaryId && salaryId !== currentSalaryId) {
      setCurrentSalaryId(salaryId);
      setIsLoading(true);
    }
  }, [salaryId]);

  // Fetch salaries in batches for navigation
  const fetchSalaryBatch = async (page: number = 1, append: boolean = false) => {
    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      if (append) setLoadingMore(true);

      try {
        const result: any = await dispatch(fetchSalaries({
          accountId,
        }));

        const newSalaries = result.payload || [];
        setHasMoreData(newSalaries.length === pageSize);

        console.log(`Fetched ${newSalaries.length} salaries for page ${page}`);
      } catch (error) {
        console.error('Error fetching salaries:', error);
        setHasMoreData(false);
      } finally {
        if (append) setLoadingMore(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    setCurrentPage(1);
    fetchSalaryBatch(1, false);
  }, [selectedAccount._id, isLoggedIn]);

  useEffect(() => {
    const fetchSalaryDetails = async () => {
      if (!currentSalaryId) return;

      try {
        setIsLoading(true);

        const detailsRes: any = await dispatch(fetchSalaryById(currentSalaryId)).unwrap();
        setSalaryDetails(detailsRes);

        try {
          const { url: s3Url } = await dispatch(fetchSalarySlipUrl({ id: currentSalaryId, refresh: true })).unwrap();
          if (s3Url) {
            setPdfUrl(s3Url);
          } else {
            const { blob } = await dispatch(downloadSalarySlip(currentSalaryId)).unwrap();
            setPdfUrl(window.URL.createObjectURL(blob));
          }
        } catch (urlErr) {
          const { blob } = await dispatch(downloadSalarySlip(currentSalaryId)).unwrap();
          setPdfUrl(window.URL.createObjectURL(blob));
        }

      } catch (error) {
        console.error('Failed to fetch salary details:', error);
        toast.error('Failed to load salary details');
      } finally {
        setIsLoading(false);
      }
    };

    if (currentSalaryId) {
      fetchSalaryDetails();
    }

    // Cleanup blob URL on unmount
    return () => {
      if (pdfUrl) {
        window.URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [currentSalaryId]);

  const handleSalaryChange = (newSalaryId: string) => {
    if (newSalaryId !== currentSalaryId) {
      setCurrentSalaryId(newSalaryId);
      setIsLoading(true);
      setPdfUrl(''); // Clear old PDF

      // Update URL with new salary ID
      navigate(`/salary/${newSalaryId}/view`, {
        replace: true
      });
    }
  };

  // Handle loading more data when needed
  const handleLoadMore = () => {
    if (hasMoreData && !loadingMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      fetchSalaryBatch(nextPage, true);
    }
  };

  // Handle direct salary search - search by TXN ID
  const handleDirectSalarySearch = async (txnId: string): Promise<boolean> => {
    if (!txnId.trim()) return false;

    const { _id: accountId } = selectedAccount;
    if (accountId && isLoggedIn) {
      try {
        // Search for salaries matching the TXN ID
        const foundSalaries = salaries.filter(sal =>
          sal.txnId && sal.txnId.toString().includes(txnId)
        );

        if (foundSalaries.length > 0) {
          console.log(`Found ${foundSalaries.length} salaries matching "${txnId}"`);

          // Load the first matching salary
          const firstSalary = foundSalaries[0];

          // Navigate to the first salary
          handleSalaryChange(firstSalary._id);

          return true;
        } else {
          alert(`No salaries found matching "${txnId}". Please try a different search.`);
          return false;
        }
      } catch (error) {
        console.error('Error searching salary:', error);
        alert('Error searching for salary. Please try again.');
        return false;
      }
    }
    return false;
  };

  const handleDownloadSlip = async (salId: string, employeeName: string) => {
    try {
      const { blob } = await dispatch(downloadSalarySlip(salId)).unwrap();

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Salary_Slip_${employeeName.replace(/\s+/g, '_')}_${moment().format('YYYY_MM_DD')}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Salary slip downloaded successfully');
    } catch (error) {
      console.error('Failed to download salary slip:', error);
      toast.error('Failed to download salary slip');
    }
  };

  const handleMarkAsPaid = async (salary: any) => {
    try {
      const paymentMode = salary.paymentMode || 'CASH';

      const paymentData: any = {
        id: salary._id,
        paymentDate: new Date(),
        paymentMode: paymentMode,
      };

      if (paymentMode !== 'CASH' && salary.fromBankAccount) {
        paymentData.fromBankAccount = typeof salary.fromBankAccount === 'string'
          ? salary.fromBankAccount
          : (salary.fromBankAccount as any)._id;
      }

      // Call appropriate API based on payment type
      if (salary.paymentType === 'DAILY_WAGE') {
        await dispatch(payDailyWage(paymentData)).unwrap();
      } else {
        await dispatch(paySalary(paymentData)).unwrap();
      }

      toast.success('Salary marked as paid successfully');
      const refreshed: any = await dispatch(fetchSalaryById(salary._id)).unwrap();
      setSalaryDetails(refreshed);
      fetchSalaryBatch(1, false); // Refresh the list
    } catch (error) {
      console.error('Failed to mark as paid:', error);
      toast.error('Failed to mark as paid. Please try again.');
    }
  };

  const handleDeleteClick = () => {
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (currentSalaryId) {
      try {
        await dispatch(deleteSalary(currentSalaryId)).unwrap();
        toast.success('Salary payment deleted successfully');
        setDeleteDialogOpen(false);
        navigate('/salary/list');
      } catch (error) {
        console.error('Failed to delete salary:', error);
        toast.error('Failed to delete salary payment');
        setDeleteDialogOpen(false);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
  };

  const handleShare = (salary: any) => {
    setSelectedSalaryForShare(salary);
    setShareDrawerOpen(true);
  };

  const handleShareDrawerClose = () => {
    setShareDrawerOpen(false);
    setSelectedSalaryForShare(null);
  };

  const getTdsLabel = (salary: any) => {
    const cfg = salary?.taxConfig?.tdsConfig;
    if (cfg?.label) return `TDS (${cfg.label})`;
    if (cfg?.rate) return `TDS (${cfg.rate}%)`;
    return 'TDS';
  };

  const isBasicOrHraEarning = (label = '') => /^basic/i.test(label) || /^(hra|house\s*rent)/i.test(label);

  return (
    <>
      <Box sx={{
        backgroundColor: '#fafafb',
        minHeight: '100vh',
        pt: 0,
        pb: 1
      }}>
        {/* Top Section with Navigation Bar and Quick Stats */}
        {isLoggedIn && (
          <Box sx={{ px: { xs: 1, sm: 2 }, pt: { xs: 1, sm: 2 }, pb: 0.5 }}>
            <Grid container spacing={2}>
              {/* Navigation Bar and Actions */}
              <Grid item xs={12} md={12}>
                {(salaries && salaries.length > 0 && currentSalaryId) && (
                  <SalaryNavigationBar
                    currentSalaryId={currentSalaryId || ''}
                    onSalaryChange={handleSalaryChange}
                    salaries={salaries}
                    currentSalaryDetails={salaryDetails}
                    onLoadMore={handleLoadMore}
                    hasMoreData={hasMoreData}
                    loadingMore={loadingMore}
                    onDirectSearch={handleDirectSalarySearch}
                    onDownload={handleDownloadSlip}
                    onShare={handleShare}
                    onMarkAsPaid={handleMarkAsPaid}
                    onDelete={handleDeleteClick}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        )}

        <Grid container spacing={2} sx={{
          px: { xs: 1, sm: 2 },
          pt: 1
        }}>
          {/* Left side - PDF viewer */}
          <Grid item xs={12} md={8} sx={{
            order: { xs: 2, md: 1 }
          }}>
            {isLoading ? (
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                p: 3,
                height: { xs: '88vh', md: 'calc(100vh - 110px)' }
              }}>
                <Grid container>
                  <Grid item xs={12} style={{ textAlign: 'center' }}>
                    <CircularProgress color="info" />
                    <Typography sx={{ mt: 2 }}>Loading Salary Slip...</Typography>
                  </Grid>
                  <Grid item xs={12}>
                    <Skeleton width="100%" height={300} />
                  </Grid>
                </Grid>
              </Box>
            ) : (
              <Box sx={{
                backgroundColor: '#fff',
                borderRadius: '12px',
                overflow: 'hidden',
                height: { xs: '88vh', md: 'calc(100vh - 110px)' }
              }}>
                <object
                  style={{ height: "100%", width: "100%" }}
                  data={pdfUrl}
                  type="application/pdf"
                />
              </Box>
            )}
          </Grid>

          {/* Right side - Salary Details */}
          <Grid item xs={12} md={4} sx={{ order: { xs: 1, md: 2 } }}>

            {/* Quick Stats */}
            <Grid item xs={12} md={12} sx={{ pb: 2 }}>
              {salaries && salaries.length > 0 && (
                <Paper
                  elevation={0}
                  sx={{
                    p: 1.5,
                    m: 0,
                    border: '1px solid #e9ecf1',
                    borderRadius: '12px',
                    backgroundColor: '#fff',
                    height: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  <Stack direction="row" spacing={2} sx={{ width: '100%' }}>
                    {/* Total Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      pr: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'text.primary',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Total
                      </Typography>
                    </Box>

                    {/* Pending Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      borderRight: '1px solid #e9ecf1',
                      px: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'warning.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.filter(sal => sal.paymentStatus !== 'PAID').length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Pending
                      </Typography>
                    </Box>

                    {/* Paid Salaries */}
                    <Box sx={{
                      flex: 1,
                      textAlign: 'center',
                      pl: 1
                    }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontWeight: 700,
                          color: 'success.main',
                          mb: 0.25,
                          lineHeight: 1.2
                        }}
                      >
                        {salaries.filter(sal => sal.paymentStatus === 'PAID').length}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.secondary',
                          fontWeight: 500,
                          fontSize: '0.75rem'
                        }}
                      >
                        Paid
                      </Typography>
                    </Box>
                  </Stack>
                </Paper>
              )}
            </Grid>

            
            {/* Monthly Salary Slip Details Section */}
            <Box sx={{
              borderRadius: '12px',
              border: '1px solid #e9ecf1',
              backgroundColor: '#fff',
              display: { xs: 'none', sm: 'block' }
            }}>
              <Box sx={{
                bgcolor: '#f5f5f5',
                p: { xs: 1.5, sm: 2 },
                borderTopLeftRadius: '12px',
                borderTopRightRadius: '12px'
              }}>
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    fontSize: { xs: '1.1rem', sm: '1.25rem' }
                  }}
                >
                  {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'Monthly Salary' : 'Daily Wage'} Slip
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
                >
                  {salaryDetails?.data?.createdAt
                    ? moment(salaryDetails.data.createdAt).format('DD MMM YYYY')
                    : 'N/A'} • TXN #{salaryDetails?.data?.txnId} • {salaryDetails?.data?.paymentStatus}
                </Typography>
              </Box>
              <Box sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack spacing={{ xs: 1.5, sm: 2 }}>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Employee Name
                    </Typography>
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {salaryDetails?.data?.employee?.name || 'N/A'}
                    </Typography>
                  </Box>
                  <Divider sx={{ my: 2 }} />

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Total Payable Amount
                    </Typography>
                    <Typography
                      variant="h5"
                      sx={{
                        fontWeight: 600,
                        color: 'primary.main',
                        fontSize: { xs: '1.25rem', sm: '1.5rem' }
                      }}
                    >
                      {Utils.formatNumber(
                        (parseFloat(salaryDetails?.data?.netSalary || 0) +
                          (salaryDetails?.data?.expensePayments || []).reduce((sum: number, e: any) => sum + (e.amount || 0), 0)
                        ).toFixed(2), true, true
                      )}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Period
                    </Typography>
                    <Typography variant="body1">
                      {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY'
                        ? salaryDetails?.data?.paymentPeriod?.month || 'N/A'
                        : salaryDetails?.data?.paymentPeriod?.fromDate && salaryDetails?.data?.paymentPeriod?.toDate
                          ? `${moment(salaryDetails.data.paymentPeriod.fromDate).format('DD/MM/YYYY')} - ${moment(salaryDetails.data.paymentPeriod.toDate).format('DD/MM/YYYY')}`
                          : 'N/A'
                      }
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Status
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{
                        fontWeight: 500,
                        color: salaryDetails?.data?.paymentStatus === 'PAID'
                          ? 'success.main'
                          : 'warning.main'
                      }}
                    >
                      {salaryDetails?.data?.paymentStatus || 'N/A'}
                    </Typography>
                  </Box>

                  <Box>
                    <Typography variant="body2" color="text.secondary">
                      Payment Type
                    </Typography>
                    <Typography variant="body1">
                      {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'Monthly Salary' : 'Daily Wage'}
                    </Typography>
                  </Box>

                </Stack>
              </Box>
            </Box>

            {/* Salary Breakdown Section */}
            {!isLoading && salaryDetails?.data && (
              <Box sx={{
                borderRadius: '12px',
                border: '1px solid #e9ecf1',
                backgroundColor: '#fff',
                mt: 2,
                mb: 2,
                overflowY: 'auto'
              }}>
                <Box sx={{
                  bgcolor: '#f5f5f5',
                  p: 2,
                  borderTopLeftRadius: '12px',
                  borderTopRightRadius: '12px',
                  position: 'sticky',
                  top: 0,
                  zIndex: 1
                }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, fontSize: '1rem' }}>
                    Salary Breakdown
                  </Typography>
                </Box>
                <Box sx={{ p: 2 }}>
                  <Stack spacing={2}>
                    {/* Earnings Section */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, bgcolor: '#f0f9ff', p: 1, borderRadius: 1 }}>
                        Earnings
                      </Typography>
                      <Stack spacing={1}>
                        {Number(salaryDetails.data.earnings?.basic || 0) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Basic:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.earnings.basic).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {Number(salaryDetails.data.earnings?.hra || 0) > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">HRA:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.earnings.hra).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.earnings?.additionalEarnings
                          ?.filter((earning: any) => !isBasicOrHraEarning(earning.label || ''))
                          .map((earning: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{earning.label}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(earning.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        {salaryDetails.data.earnings?.allowances?.map((allowance: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{allowance.headName}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(allowance.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Earnings:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#059669' }}>
                            {Utils.formatNumber(parseFloat(salaryDetails.data.grossSalary || salaryDetails.data.earnings?.totalEarnings || 0).toFixed(2), true, true)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Deductions Section */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, bgcolor: '#fef2f2', p: 1, borderRadius: 1 }}>
                        Deductions
                      </Typography>
                      <Stack spacing={1}>
                        {salaryDetails.data.lopAmount > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">
                              Loss of Pay ({salaryDetails.data.lopDays || 0} days):
                            </Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.lopAmount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.pf > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">PF:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.pf).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.esi > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">ESI:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.esi).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.tds > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{getTdsLabel(salaryDetails.data)}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.tds).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.professionalTax > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Professional Tax:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.professionalTax).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.advance > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Advance:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.advance).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.loan > 0 && (
                          <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">Loan:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(salaryDetails.data.deductions.loan).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        )}
                        {salaryDetails.data.deductions?.otherDeductions?.map((deduction: any, idx: number) => (
                          <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between' }}>
                            <Typography variant="body2" color="text.secondary">{deduction.headName}:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                              {Utils.formatNumber(parseFloat(deduction.amount).toFixed(2), true, true)}
                            </Typography>
                          </Box>
                        ))}
                        <Divider />
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Deductions:</Typography>
                          <Typography variant="body2" sx={{ fontWeight: 700, color: '#dc2626' }}>
                            {Utils.formatNumber((parseFloat(salaryDetails.data.deductions?.totalDeductions || 0) + parseFloat(salaryDetails.data.lopAmount || 0)).toFixed(2), true, true)}
                          </Typography>
                        </Box>
                      </Stack>
                    </Box>

                    {/* Expense Add-ons Section - before net payable */}
                    {salaryDetails.data.expensePayments && salaryDetails.data.expensePayments.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#0f172a', mb: 1.5, bgcolor: '#fef3c7', p: 1, borderRadius: 1 }}>
                          Expense Add-ons ({salaryDetails.data.expensePayments.length})
                        </Typography>
                        <Stack spacing={1}>
                          {salaryDetails.data.expensePayments.map((exp: any, idx: number) => (
                            <Box key={idx} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Box>
                                <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.8rem' }}>
                                  {exp.referenceId || `EXP-${String(exp.txnId).padStart(3, '0')}`}
                                  {exp.expenseCategory?.name ? ` • ${exp.expenseCategory.name}` : ''}
                                </Typography>
                                {exp.paymentDate && (
                                  <Typography variant="caption" color="text.secondary">
                                    {moment(exp.paymentDate).format('DD MMM YYYY')}
                                  </Typography>
                                )}
                              </Box>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {Utils.formatNumber(parseFloat(exp.amount || 0).toFixed(2), true, true)}
                              </Typography>
                            </Box>
                          ))}
                          <Divider />
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 0.5 }}>
                            <Typography variant="body2" sx={{ fontWeight: 700 }}>Total Expenses:</Typography>
                            <Typography variant="body2" sx={{ fontWeight: 700, color: '#92400e' }}>
                              {Utils.formatNumber(
                                salaryDetails.data.expensePayments
                                  .reduce((sum: number, e: any) => sum + (e.amount || 0), 0)
                                  .toFixed(2),
                                true, true
                              )}
                            </Typography>
                          </Box>
                        </Stack>
                      </Box>
                    )}

                    {/* Net Salary */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>Net Salary:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {Utils.formatNumber(parseFloat(salaryDetails.data.netSalary || 0).toFixed(2), true, true)}
                      </Typography>
                    </Box>

                    {/* Total Payable */}
                    <Box sx={{ bgcolor: '#ecfdf5', p: 1.5, borderRadius: 1, border: '1px solid #a7f3d0' }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#065f46' }}>Total Payable:</Typography>
                        <Typography variant="body1" sx={{ fontWeight: 700, color: '#059669' }}>
                          {Utils.formatNumber(
                            (parseFloat(salaryDetails.data.netSalary || 0) +
                              (salaryDetails.data.expensePayments || []).reduce((sum: number, e: any) => sum + (e.amount || 0), 0)
                            ).toFixed(2), true, true
                          )}
                        </Typography>
                      </Box>
                    </Box>
                  </Stack>
                </Box>
              </Box>
            )}

          </Grid>
        </Grid>
      </Box>

      {/* Share Salary Slip Drawer */}
      <Drawer
        anchor="right"
        open={shareDrawerOpen}
        onClose={handleShareDrawerClose}
      >
        {selectedSalaryForShare?.data && (
          <DialogShareSalarySlip
            salaryId={selectedSalaryForShare.data._id}
            employeeName={selectedSalaryForShare.data.employee?.name || ''}
            employeeEmail={selectedSalaryForShare.data.employee?.personalEmail || ''}
            employeePhone={selectedSalaryForShare.data.employee?.primaryPhone || ''}
            openDrawer={shareDrawerOpen}
            onClose={handleShareDrawerClose}
          />
        )}
      </Drawer>

      {/* Modern Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
        aria-describedby="delete-dialog-description"
        maxWidth="sm"
        fullWidth
        TransitionProps={{
          timeout: {
            enter: 400,
            exit: 200,
          },
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)',
            transform: deleteDialogOpen ? 'scale(1)' : 'scale(0.95)',
            transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
          }
        }}
        sx={{
          '& .MuiBackdrop-root': {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            backdropFilter: 'blur(4px)',
            transition: 'all 0.3s ease-in-out',
          },
          '& .MuiDialog-paper': {
            animation: deleteDialogOpen ? 'slideInUp 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)' : 'none',
          },
          '@keyframes slideInUp': {
            '0%': {
              transform: 'translateY(60px) scale(0.9)',
              opacity: 0,
            },
            '100%': {
              transform: 'translateY(0) scale(1)',
              opacity: 1,
            },
          },
        }}
      >
        <DialogTitle
          id="delete-dialog-title"
          sx={{
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
            py: 3,
            px: 3
          }}
        >
          <Box sx={{
            backgroundColor: '#fee2e2',
            borderRadius: '50%',
            p: 1.5,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <DeleteIcon sx={{ color: '#dc2626', fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{
              fontWeight: 600,
              color: '#1f2937',
              fontSize: '1.25rem',
              lineHeight: 1.2
            }}>
              Delete {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'Salary' : 'Daily Wage'} Payment
            </Typography>
            <Typography variant="body2" sx={{
              color: '#6b7280',
              mt: 0.5,
              fontSize: '0.875rem'
            }}>
              This action cannot be undone
            </Typography>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ py: 4, px: 3 }}>
          <Typography
            variant="body1"
            sx={{
              color: '#1f2937',
              fontSize: '1.125rem',
              fontWeight: 400,
              lineHeight: 1.7,
              letterSpacing: '0.01em',
              pt: 2,
              pb: 1,
              mb: 3
            }}
          >
            Are you sure you want to delete this {salaryDetails?.data?.paymentType === 'MONTHLY_SALARY' ? 'salary' : 'daily wage'} payment record?
          </Typography>
          {salaryDetails?.data && (
            <Typography
              variant="body2"
              sx={{
                color: '#6b7280',
                fontSize: '0.95rem',
                lineHeight: 1.6,
                mb: 3
              }}
            >
              Payment Amount: <strong>{Utils.formatNumber(parseFloat(salaryDetails.data.netSalary) || 0, true, true)}</strong><br />
              Employee: <strong>{salaryDetails.data.employee?.name || '-'}</strong>
            </Typography>
          )}

          <Box sx={{
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: 2,
            p: 3,
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2
          }}>
            <Box sx={{
              backgroundColor: '#fbbf24',
              borderRadius: '50%',
              p: 0.5,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minWidth: 24,
              height: 24
            }}>
              <Typography sx={{
                color: '#ffffff',
                fontSize: '0.875rem',
                fontWeight: 'bold'
              }}>
                !
              </Typography>
            </Box>
            <Box>
              <Typography variant="subtitle2" sx={{
                fontWeight: 600,
                color: '#92400e',
                fontSize: '0.875rem',
                mb: 0.5
              }}>
                Important Warning
              </Typography>
              <Typography variant="body2" sx={{
                color: '#92400e',
                fontSize: '0.875rem',
                lineHeight: 1.5
              }}>
                This salary payment and all associated breakdown data will be permanently removed. This data cannot be recovered once deleted.
              </Typography>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{
          backgroundColor: '#f9fafb',
          borderTop: '1px solid #e5e7eb',
          px: 3,
          py: 2.5,
          gap: 1.5
        }}>
          <Button
            onClick={handleDeleteCancel}
            variant="outlined"
            sx={{
              minWidth: 100,
              height: 40,
              textTransform: 'none',
              fontWeight: 500,
              borderColor: '#d1d5db',
              color: '#374151',
              '&:hover': {
                borderColor: '#6b7280',
                backgroundColor: '#e5e7eb',
                color: '#1f2937'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            variant="contained"
            startIcon={<DeleteIcon sx={{ fontSize: 18 }} />}
            sx={{
              minWidth: 140,
              height: 40,
              textTransform: 'none',
              fontWeight: 600,
              backgroundColor: '#dc2626',
              '&:hover': {
                backgroundColor: '#b91c1c'
              },
              boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)'
            }}
          >
            Delete Payment
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default SalaryView;
